import get from "lodash/get";
import { useActivityFormStore } from "PFApp/activities/activity_form/activity_form_store";
import { isFieldBlank } from "PFApp/activities/activity_form/helpers";
import { useSectionsUtils } from "PFApp/activities/activity_form/hooks/use_sections_utils";
import { useSubtemplate } from "PFApp/activities/activity_form/hooks/use_subtemplate";
import { FieldFactory } from "PFApp/activities/activity_form/parts/components/field_factory/field_factory";
import {
  FIELD_CUSTOM_FIELDS_KEY,
  FIELD_METADATA_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { useFormValidation } from "PFApp/hooks/activity_form/use_form_validation";
import { Sections } from "PFApp/types/activity_form/activity_form_schema";
import { Divider } from "PFComponents/divider";
import { Typography } from "PFComponents/typography";
import { ParsedSchemaProperty } from "PFCore/types";
import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import css from "./segment_section.module.scss";

export const SegmentSection = () => {
  const { t } = useTranslation("activities", { keyPrefix: "form" });
  const { getSectionFieldsBy } = useSectionsUtils();
  const template = useActivityFormStore((state) => state.template);
  const { draft: submittedAsDraft } = useActivityFormStore((state) => state.submitOptions);
  const subtemplate = useSubtemplate();
  const { isFieldRequired } = useFormValidation({ subtemplate, template });
  const {
    control,
    formState: { errors }
  } = useFormContext();

  const segmentFields = useMemo(
    () => getSectionFieldsBy(Sections.BasicInformation, "segment"),
    [getSectionFieldsBy]
  );

  return (
    <>
      <Divider className={css.divider} />
      <Typography variant="bodyBold">{t("sections.segment.title")}</Typography>
      <Typography variant="labelRegular">{t("sections.segment.description")}</Typography>
      {segmentFields.map((field) => {
        const property = field.property as ParsedSchemaProperty;
        const isCustomField = property.type === "custom_field";
        const rootFieldKey = isCustomField ? FIELD_CUSTOM_FIELDS_KEY : FIELD_METADATA_KEY;
        const required = isFieldRequired(field);

        return (
          <Controller
            key={property.name}
            name={`${rootFieldKey}.${property.name}`}
            control={control}
            rules={{
              validate: (value: string) => {
                if (!submittedAsDraft && required && isFieldBlank(value)) {
                  return t("errors.cantBeBlank");
                }
                return true;
              }
            }}
            render={({ field }) => (
              <FieldFactory
                property={property}
                value={field.value || (isCustomField ? [] : undefined)}
                error={
                  (get(errors, field.name)?.message as string) ||
                  (get(errors, property.name)?.message as string)
                }
                onChange={field.onChange}
                required={required}
              />
            )}
          />
        );
      })}
    </>
  );
};
